<template>
    <div class="textSet-column c-textSetColumn q-mt-sm">
        <div style="display: flex; flex-direction: column;">
            <div v-if="(!initial || initial.usage == 1) && item.SourceType == 'userIDColumn'" class="row items-start q-mb-sm">
                <q-toggle dense v-model="usecurrentuser" color="primary" class="q-ml-none" />
                <div class="q-ml-sm col">
                    <span>{{ Translate('Constrain to current user only') }}</span>
                    <q-icon name="mdi-information" size="sm" class="q-ml-xs cursor-pointer form-hint-icon">
                        <q-tooltip anchor="bottom middle" self="top middle">
                            {{ Translate('Limits results to the user viewing the report. This setting does not apply when the report is scheduled.') }}
                        </q-tooltip>
                    </q-icon>
                </div>
            </div>

            <div v-if="(!initial || initial.usage < 2) && !usecurrentuser && allowStandardTextFilter && advancedSettings" class="row items-start q-mb-xs">
                <q-toggle dense v-model="usefulltext" color="primary" class="q-ml-none" />
                <div class="q-ml-sm col">
                    <span>{{ Translate('Use conditional filtering') }}</span>
                    <q-icon name="mdi-information" size="sm" class="q-ml-xs cursor-pointer form-hint-icon">
                        <q-tooltip anchor="bottom middle" self="top middle" :delay="50">
                            <div style="white-space: normal; word-wrap: break-word; max-width: 400px;">{{ standardTextFilterTooltip }}</div>
                        </q-tooltip>
                    </q-icon>
                </div>
            </div>
        </div>

        <q-banner v-if="usefulltext && haslookup" dense rounded class="warning-banner q-my-sm">
            <template #avatar>
                <q-icon name="mdi-alert" size="sm"/>
            </template>
            {{ Translate('This field displays values from a corresponding lookup table, translating internal identifiers (which may be GUIDs) into readable text. This mode cannot be used to search for readable text.') }}
        </q-banner>
        <SearchableTextColumn v-if="usefulltext && !usecurrentuser"
                              :item="item"
                              :settings="settings"
                              :isopen="isopen"
                              :value="value"
                              @change="relayChange"></SearchableTextColumn>
        <div v-else>
            <div style="display: flex; flex-direction: row;" class="q-mb-sm" v-if="!usecurrentuser">
                <q-input outlined dense type="text" :placeholder="Translate('Search...')" style="max-width: 300px; width: 300px;" v-model="search" class="_search-box q-ml-xs q-mr-sm q-mt-sm" bg-color="white" hide-bottom-space />
                <div v-if="UniqueValuesVisible && advancedSettings" class="row items-center q-my-md">
                    <q-checkbox dense v-model="showunique" color="primary" class="q-pt-none" />
                    <span class="q-ml-xs">{{ Translate('Hide unused items') }}</span>
                    <q-icon name="mdi-information" size="sm" class="q-ml-xs cursor-pointer form-hint-icon">
                        <q-tooltip anchor="bottom middle" self="top middle" :delay="50">
                            {{ Translate('When on, only list items that were used in the date range selected.') }}
                        </q-tooltip>
                    </q-icon>
                </div>
                <q-spinner v-if="query_in_progress" color="primary" size="20px" class="q-ml-sm q-mt-sm" />
                <q-icon v-if="queryError" size="sm" color="negative" :title="queryError" name="mdi-alert-octagram-outline" class="q-ml-sm q-mt-sm" />
            </div>

            <q-item class="mediumdense" clickable v-ripple @click="selectAll" v-if="!usecurrentuser">
                <q-item-section avatar>
                    <q-icon :color="isAllSelected ? 'primary' : undefined" :name="isAllSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'" />
                </q-item-section>

                <q-item-section class="removepadding">
                    {{ Translate('All') }}
                </q-item-section>
            </q-item>

            <q-list class="q-mb-md q-pa-none" style="max-height: 220px; overflow: auto;" v-if="!usecurrentuser">
                <q-item
                    v-for="row in filteredlist"
                    :key="row"
                    dense
                    clickable
                    v-ripple
                    class="mediumdense q-pt-none q-pb-none"
                    @click="toggleListItem(row)"
                >
                    <q-item-section avatar>
                        <q-icon :color="isChecked(row) ? 'primary' : undefined" :name="isChecked(row) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'" />
                    </q-item-section>

                    <q-item-section class="text-no-wrap removepadding">
                        {{ transformValue(row) }}
                    </q-item-section>
                </q-item>
            </q-list>
        </div>
    </div>
</template>

<script>
    import utils from '@/Shared/utils.jsx';
    import helps from '@/Application/careHelpfulFunctions.jsx';
    import common from './common.js';
    import _ from 'lodash';

    import { appSettings } from '@/Shared/appSettings';

    import SearchableTextColumn from './filter_searchableTextColumn.vue';
    import methods from '@/Shared/methods';
    import {
        QToggle, QIcon, QTooltip, QBanner, QInput, QCheckbox, QSpinner, QList, QItem, QItemSection,
    } from 'quasar';

    export default {
        components: {
            SearchableTextColumn,
            QToggle, QIcon, QTooltip, QBanner, QInput, QCheckbox, QSpinner, QList, QItem, QItemSection,
        },
        // Required in Vue 3 so parent @change is the component emit, not a native
        // DOM listener — otherwise blur/click on the grid delivers an Event and
        // localUpdateFilterValue treats Event.type as filter criteria (CCDEV-18136).
        emits: ['change'],
        data: function () {
            return {
                usefulltext: false,
                usecurrentuser: false,
                values: {},
                selected_items: [],
                hidden_selected_items: [],
                selected_filtered_items: [],
                search: '',
                listitems: [],
                showunique: true,
                query_in_progress: false,

                expn: null,
                isUserID: false,

                isAllSelected: false,
                useExclusive: false,
                queryError: '',
            }
        },
        props: {
            item: Object,
            settings: null,
            value: null,
            initial: null,
            isopen: null,
            advancedSettings: null,
        },
        created() {
            this.isUserID = this.item.SourceType == 'userIDColumn';
            this.showunique = (this.item.SourceType != 'userIDColumn' && this.item.SourceType != 'customerIDColumn') &&
                (this.item.MappingTableType == 'None' || (!this.item.MappingTableType && !this.item.DBLookupList));

            if (this.initial && this.initial.usage == 2 && this.initial.mode != 'exclusive') {
                this.listitems = this.initial.values;
            }
            else
            {
                   this.loadColumnValues();
            }
            this.initializeFromValue(this.value);
        },
        computed: {
            isDEBUG: function () {
                return appSettings.DEBUG;
            },            
            filteredlist: function () {
                let results;         

                const s = this.search.toLowerCase();                
                if (this.search.length > 0)
                    results =  this.listitems.filter(a => this.transformValue(a).toLowerCase().includes(s));
                else
                    results = this.listitems;
                
                var newlyHiddenItems = this.selected_filtered_items.filter(item => !results.includes(item));
                var nowPresentItems = this.hidden_selected_items.filter(item => results.includes(item));
                this.selected_filtered_items = [...this.selected_filtered_items, ...nowPresentItems];
                this.hidden_selected_items =  this.hidden_selected_items.filter(item => !nowPresentItems.includes(item));
                this.hidden_selected_items = [...this.hidden_selected_items, ...newlyHiddenItems];

                return results;
            },
            allDescription: function () {
                if (this.isAllSelected)
                    return 'All unselected items will become an exclusion list. This';
                else
                    return '(Selection is an inclusion list)';
            },
            allowStandardTextFilter: function () {
                return (this.item.MappingTableType != 'LookupAPI' || !this.item.CompoundFilter) && (!this.haslookup || this.isDEBUG);
            },

            UniqueValuesVisible: function () {
                return (this.item.MappingTableType == 'LookupAPI' || (!this.item.MappingTableType && this.item.DBLookupList) ||
                    this.item.SourceType == 'userIDColumn' || this.item.SourceType == 'customerIDColumn') &&
                    (!this.initial || this.initial.usage < 2) && !this.item.CompoundFilter;
            },
            standardTextFilterTooltip: function () {
                return this.Translate(`Switch to a mode where you define the filter using conditional values (for example, a queue name filter CONTAINS 'Sales' OR CONTAINS 'Support')`);
            },
            haslookup: function () {
                return (this.item.SourceType == 'userIDColumn' || this.item.SourceType == 'customerIDColumn' ||
                    (this.item.lookups && Object.keys(this.item.lookups).some(k => this.item.lookups[k] != k)));
            },
        },
        watch: {
            showunique: function (newv) {
                this.loadColumnValues();
            },            
            selected_items: function (newv) {
                this.updateFilter(newv);
            },
            selected_filtered_items: function (newv) {
                this.updateSelectedFilteredItems(newv);
            },
            usecurrentuser: function (newv) {         
                this.updateFilter(this.selected_items);
            },
            usefulltext: function (newv) {
                if (!newv)
                    this.updateFilter(this.selected_items);
            },
            useExclusive: function (newv) {
                this.updateFilter(this.selected_items);
            },
            advancedSettings: function (newv) {
                if (!newv) {
                    this.usefulltext = false;
                    this.showunique = false;
                }
            },
            isopen: function(newv) {
                console.log(`textSetColumn(${this.item.ColId}) is ${newv ? 'open' : 'closed'}`);
                if (newv)
                    this.loadColumnValues();
            },
            value: {
                handler(newValue) {
                       this.syncFromProps(newValue);
                },
                deep: true,
                immediate: true 
            },
        },
        methods: {
            ...methods,
             toggleListItem(item) {
                const arr = [...this.selected_filtered_items];
                const idx = arr.indexOf(item);
                if (idx >= 0) arr.splice(idx, 1);
                else arr.push(item);
                this.selected_filtered_items = arr;
            },
             syncFromProps(value) {
                if (!value || Object.keys(value).length === 0 || value.cleared == 1) {
                    if (this.selected_items.length > 0 || this.selected_filtered_items.length > 0)                                        
                    {
                        if (this.initial && this.initial.usage == 2 && this.initial.mode != 'exclusive') {
                            this.selected_items = [];
                            this.selected_filtered_items = [];
                        }
                        else 
                        {
                            this.selected_items = [];
                            this.selected_filtered_items = [];
                        }
                    }
                }
            },
            initializeFromValue(value) {
                if (value && value.filterType == 'text' && value.type == 'equals' && value.filter == '{{CurrentUserID}}')
                {
                    this.usecurrentuser = true;
                }
                else if (value && value.filterType == 'text')
                {
                    this.usefulltext = true;
                }    

                if (!this.initial && value && value.values)
                {
                    this.selected_items = value.values || [];
                }
                else if (this.initial && this.initial.usage == 1)
                {
                    if (value.cleared)
                    {
                       this.selected_items = [];
                    }
                    else 
                    {
                        this.selected_items = this.initial.values || [];
                    }                
                } 
                else if (this.initial && this.initial.usage == 2 && value && value.values && !_.isEqual(this.initial, value)) {
                    this.selected_items = value.values || [];
                    this.useExclusive = value.mode == 'exclusive';
                }
                else if (!value)
                {
                    this.selected_items = [];
                }
                if (!this.initial && value)
                {
                    this.useExclusive = value.mode == 'exclusive';
                }
                else if (this.initial && this.initial.usage == 1)
                {
                    this.useExclusive = value.mode == 'exclusive';
                }
                else if (!value)
                {
                    this.useExclusive = false;
                }
                this.isAllSelected = this.useExclusive;

                this.hidden_selected_items = this.selected_items;
                this.selected_filtered_items = [...this.selected_items];
            },
            updateSelectedFilteredItems(newv)
            {
                if (!this.useExclusive )
                {
                    if (this.hidden_selected_items)
                    {
                        newv =  [...newv, ...this.hidden_selected_items];
                    }
                } 
                else 
                {
                    var itemsToExclude = [...newv, ...this.hidden_selected_items];

                    if(!this.isAllSelected)
                        newv = this.listitems.filter(item => !itemsToExclude.includes(item));
                    else
                        newv = itemsToExclude;
                }                
               this.updateFilter([...new Set(newv)]);              
            },
            updateFilter(newv) {
                if (this.initial && this.initial.usage == 2 && newv.length)
                { 
                    this.$emit('change', {
                        filterType: "set",
                        values: newv,
                        mode: 'inclusive'
                    });
                }
                else if (this.initial && this.initial.usage == 2)
                {
                    if (this.value && this.value.cleared)
                    {               
                        this.$emit('change', {
                            filterType: "set",
                            values: this.initial.values,
                            mode: this.initial.mode
                        });
                    }
                    else
                    {
                        this.$emit('change', {
                            filterType: "set",
                            usage: this.initial.usage,
                            cleared: 1,
                            values: this.initial.values,
                            mode: this.initial.mode,
                        });
                    }
                }
                else if (this.usecurrentuser)
                {           
                     this.$emit('change', {
                        filterType: "text",
                        type: "equals",
                        filter: "{{CurrentUserID}}",
                        usage: 0
                    });
                }
                else if ((!newv || (newv.length == this.listitems.length && !this.item.CompoundFilter && !this.useExclusive) || newv.length < 1) && (!this.initial || this.initial.usage < 2))
                {    
                    this.$emit('change', {});
                }
                else
                {    
                    this.$emit('change', {
                        filterType: "set",
                        values: newv,
                        mode: this.useExclusive ? 'exclusive' : 'inclusive',
                    });
                }
            },
            async loadColumnValues() {
                if (this.initial && this.initial.usage == 2 && this.initial.mode != 'exclusive') return;

                this.query_in_progress = true;
                this.queryError = '';

                console.log(`loadColumnValues(colId:${this.item.ColId}) DBLookupList:${this.item.DBLookupList}, showunique:${this.showunique}`);

                if (this.showunique) {
                    const dataurl = `${common.buildRequest('Apps/ReportStudio/AdvancedReporting/GetUniqueColValues', this.settings.startDate, this.settings.endDate)}&Column=${this.item.ColId}&Sources=${encodeURIComponent(JSON.stringify(this.item.Sources))}`;
                    console.log(dataurl);

                    try {
                        const res = await utils.api.get(dataurl);
                        let lookups = [];
                        if (this.item.MappingTableType == 'LookupList' && this.item.lookups)
                            lookups = Object.keys(this.item.lookups);

                        this.listitems = [...new Set([...res.Values, ...lookups, ...this.selected_items])].toSorted((a, b) => this.byTransformedField(a, b));
                    }
                    catch (ex) {
                        this.queryError = ex.message || ex.reasonPhrase;
                        console.error(`Failed to request unique column values (${dataurl}): ${ex.message || ex.reasonPhrase}`);
                    }
                }
                else
                    switch (this.item.SourceType) {
                        case 'userIDColumn':
                            this.listitems = [ '', ...Object.keys(common.userLookup).toSorted((a, b) => this.byTransformedField(a, b)) ];
                            break;

                        case 'customerIDColumn':
                            this.listitems = [ '', ...Object.keys(common.customerLookup).toSorted((a, b) => this.byTransformedField(a, b)) ];
                            break;

                        default:
                            if ((this.item.MappingTableType == 'LookupAPI' || (!this.item.MappingTableType && this.item.DBLookupList)) && !this.showunique) {

                                await common.loadLookup(this.item);

                                if (this.item.CompoundFilter)
                                    this.listitems = [...new Set([...this.item.names.map(a => a.name), ...(this.selected_items || [])])].toSorted((a, b) => this.byTransformedField(a, b));
                                else
                                    this.listitems = [...new Set([...Object.keys(this.item.lookups), ...(this.selected_items || []), ''])].toSorted((a, b) => this.byTransformedField(a, b));
                            }
                            else if (this.item.MappingTableType == 'LookupList') {
                                this.listitems = [...new Set([...Object.keys(this.item.lookups), ...(this.selected_items || []), ''])].toSorted((a, b) => this.byTransformedField(a, b));
                            }
                            break;
                    }

                console.log(`textSetColumn filter loaded with:`);
                console.log(JSON.stringify(this.listitems, null, 3));

                if (this.initial && this.initial.usage == 2 && this.initial.mode == 'exclusive' && this.initial.values)
                    this.listitems = this.listitems.filter(a => !this.initial.values.includes(a));

                console.log(`loadColumnValues(colId:${this.item.ColId}) returned ${this.listitems.length} item(s) for ${this.item.SourceType}`);

                this.query_in_progress = false;
            },
            selectAll() {
                this.isAllSelected = !this.isAllSelected;
                this.useExclusive = this.isAllSelected;

                if (this.useExclusive)
                    this.selected_filtered_items = this.isAllSelected ? [] : this.listitems;
                else
                    this.selected_filtered_items = this.isAllSelected ? this.listitems : [];
            },
            toggleAll() {
                this.selected_filtered_items = this.listitems.filter(a => !this.selected_filtered_items.includes(a));
            },
            isChecked(item) {
                var _isChecked = false;
                if (this.useExclusive) {

                    _isChecked = !this.selected_filtered_items.includes(item);
                }
                else {
                    _isChecked =  this.selected_filtered_items.includes(item);
                }
                return _isChecked;
            },
            updateSelectedItems(items) {
                console.log(JSON.stringify(items,null,3));
            },
            toggleValue(item) {
                const next = { ...this.values };
                if (item in next)
                    delete next[item];
                else
                    next[item] = true;
                this.values = next;
            },
            transformValue(item) {
                if (!item)
                    return this.Translate('(Blank)');
                
                switch (this.item.SourceType) {
                    case 'userIDColumn':
                        return common.userLookup[item] || `(${item})`;
                    case 'channelTypeColumn': {
                        const label = item.substring(0, 14) == 'CustomChannel~' ? item.substring(14) : item;
                        return this.Translate(label);
                    }
                    case 'customerIDColumn':
                        return common.customerLookup[item] || `(${item})`;
                    case 'phoneNumColumn':
                        if (item.substring(0, 4) == 'USER')
                            return common.userLookup[item.substring(4)] || item;
                        else if (item == '+10000000000')
                            return this.Translate('(Loopback)');
                        else if (item == 'Anonymous' || item == 'anonymous')
                            return item;
                        else
                            return helps.formatPhoneNumber(item);
                    default:
                        if (this.item.lookups)
                            return this.item.lookups[item] || item;
                        else
                            return item;
                }
            },
            byTransformedField(a, b) {
                switch (this.item.SourceType) {
                    case 'userIDColumn':
                        return common.byField(common.userLookup[a] || a, common.userLookup[b] || b);
                    case 'channelTypeColumn':
                        return common.byField(a.substring(0, 14) == 'CustomChannel~' ? a.substring(14) : a, b.substring(0, 14) == 'CustomChannel~' ? b.substring(14) : b);
                    case 'customerIDColumn':
                        return common.byField(common.customerLookup[a] || a, common.customerLookup[b] || b);
                    default:
                        if (this.item.lookups)
                            return common.byField(this.item.lookups[a] || a, this.item.lookups[b] || b);
                        else
                            return common.byField(a, b);
                }
            },

            relayChange(value) {
                this.$emit('change', value);
            },
        }
    }
</script>

<style scoped>
    .verydense {
        max-height: 24px;
        min-height: 24px;
    }
    .mediumdense {
        max-height: 28px;
        min-height: 28px;
    }
    .removepadding {
        padding: 0;
    }
    .search-box {
        border: 1px solid silver;
        border-radius: 8px;
        padding-left: 3px;
        padding-bottom: 1px;
    }
    .warning-banner {
        background-color: #f6ebe1;
        color: #d67d26;
    }
</style>
